import { useEffect, useRef, useState } from "react";
import { AssistantAllButtonTypes, CodeFile } from "utils/interfaces";
import MinecraftEditorHeader from "./MinecraftEditorHeader";
import {
  EditorContext,
  MinecraftEnvironmentContext,
} from "components/utils/Contexts";
import MinecraftDemoAssistant from "./MinecraftDemoAssistant";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import InstructionsContent from "components/utils/instructions-draggable-panel/InstructionsContent";

interface MinecraftEditorEnvironmentProps {
  name: string;
  video: string;
  pdf: string;
  AIAssistant: {
    name: string;
    img: string;
    greeting: string;
    intro: string;
  };
  fileState: CodeFile[];
}

// Parameters
const HEADER_HEIGHT = 123; //px

const demoInstructions = [
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "1",
    paragraph: "-",
  },
  {
    title: "Making Space for our Base",
    paragraph: `Before we create our secret base, we need to clear some space in our world.

By filling a space with air, we can clear blocks that will be in the way of our build.

1. Go inside of blocks and grab a "fill block" Put this block inside our "on chat command" Then change the block type into an air block.
2. Because we created our sizing variables in Step 8, we can now grab these variables and use them here.
3. Go inside of variables and drag each of them into their correct positions.

For Example, Start-Width`,
    stepImage: {
      filename: "Step 9",
      hash: "Step9.svg",
    },
  },
  {
    title: "Calling the Function",
    paragraph: `Now that we have all our code inside of the function block, we need to “activate” it to be able to use it.

1. Go inside of functions and grab the "call Code" block.`,
    stepImage: {
      filename: "Step 10",
      hash: "Step10.svg",
    },
  },
];

const MinecraftEditor: React.FC<MinecraftEditorEnvironmentProps> = ({
  name,
  video,
  pdf,
  AIAssistant,
  fileState,
}) => {
  // States definition
  const [activeItem, setActiveItem] = useState<"editor" | "assistant">(
    "editor",
  ); // to change between editor and assistant views
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [assistantPopupState, setAssistantPopupState] = useState<
    "closed" | "popup" | "popupNoCode" | "expanded" | "fullscreen"
  >("closed");
  const [assistantPopupClickedButton, setAssistantPopupClickedButton] =
    useState<AssistantAllButtonTypes>(undefined);
  const [currentInstructionsStep, setCurrentInstructionsStep] = useState(8);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Reference for the header and body
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  /**
   * Function to send messages into the PXT environment
   * using the `pxteditor` controller
   * @param type
   * @param id
   */
  const pxtEditorRequest = (type: string, id?: string) => {
    // Define the iframe
    const iframe = iframeRef.current;

    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: "pxteditor",
          action: type,
          id: id,
        },
        "*",
      );
    }
  };

  /**
   * Hook used to trigger the signal to hide the simulator
   * after the iframe has been loaded
   */
  useEffect(() => {
    function receiveMessage(ev: MessageEvent) {
      // Get the content
      const resp = ev.data;
      if (
        resp &&
        resp.type === "pxthost" &&
        resp.action === "editorcontentloaded"
      ) {
        // Send the signal
        pxtEditorRequest("hidesimulator");
      }
    }

    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  return (
    <EditorContext.Provider
      value={{
        title: name,
        userFirstName: "M",
        userLastName: "E",
        pdf,
        AIAssistant,
        goToDashboard: undefined,
      }}
    >
      <MinecraftEnvironmentContext.Provider value={{ HEADER_HEIGHT }}>
        <div className="flex-grow relative">
          <div className="h-screen overflow-hidden">
            <MinecraftEditorHeader
              name={name}
              video={video}
              AIAssistant={AIAssistant}
              activeItem={activeItem}
              setActiveItem={setActiveItem}
              showAssistant={false}
              assistantPopupState={assistantPopupState}
              setAssistantPopupState={setAssistantPopupState}
              assistantPopupClickedButton={assistantPopupClickedButton}
              setAssistantPopupClickedButton={setAssistantPopupClickedButton}
              codeFiles={fileState}
            />
            <PanelGroup
              direction="horizontal"
              className="relative h-full px-3 pb-3"
              style={{ height: `calc(100vh - ${HEADER_HEIGHT}px)` }}
            >
              {/* Instructions area */}
              <Panel
                minSize={18}
                maxSize={18}
                className="h-full border-2 border-neutral-200 rounded-lg p-4 inline-flex flex-col gap-4"
              >
                <p className="text-neutral-700 text-lg font-bold">
                  Instructions
                </p>
                <InstructionsContent
                  stepByStepInstructions={demoInstructions}
                  currentInstructionsStep={currentInstructionsStep}
                  setCurrentInstructionsStep={setCurrentInstructionsStep}
                  dotsNumber={5}
                />
              </Panel>
              <PanelResizeHandle className="w-4" />
              {/* Editor */}
              <Panel
                minSize={30}
                className="w-full h-full rounded-lg overflow-visible"
              >
                <iframe
                  id="pxt-iframe"
                  ref={iframeRef}
                  className="w-full h-full"
                  src="https://minecraft.makecode.com/#test?controller=2&ws=controller&allowParentController"
                  allow="microphone; camera; fullscreen; usb; autoplay;"
                />
              </Panel>
              <PanelResizeHandle className="w-4" />
              {/* AI assistant area */}
              <Panel
                minSize={18}
                maxSize={18}
                className="h-full border-2 border-neutral-200 rounded-lg p-4 inline-flex flex-col gap-4"
              >
                {/* Assistant Panel Title */}
                <div className="flex items-center gap-1">
                  <img
                    src={AIAssistant.img}
                    alt="AI Assistant"
                    className="w-14 h-14 rounded-full"
                  />
                  <div className="flex flex-col">
                    <p className="text-neutral-700 text-lg font-bold">
                      Chat with {AIAssistant.name}
                    </p>
                    <p className="text-neutral-700 text-[0.7rem] font-light">
                      YOUR AI CODING COMPANION
                    </p>
                  </div>
                </div>
                <MinecraftDemoAssistant
                  AIAssistant={AIAssistant}
                  language={"Minecraft"}
                />
              </Panel>
            </PanelGroup>
          </div>
        </div>
      </MinecraftEnvironmentContext.Provider>
    </EditorContext.Provider>
  );
};

export default MinecraftEditor;
